import { extend } from "vee-validate";
import { required, email, alpha_dash, confirmed, oneOf, between, numeric, max, min, min_value } from "vee-validate/dist/rules";
import { localize } from "vee-validate";
import sv from "vee-validate/dist/locale/sv.json";
import pl from "vee-validate/dist/locale/pl.json";
import en from "vee-validate/dist/locale/en.json";
import no from "vee-validate/dist/locale/nb_NO.json";
import nl from "vee-validate/dist/locale/nl.json";
import $store from "@/store";
import i18n from "@/i18n";
import { isValidNoPostcode, isValidSePostcode } from "@/utils/safe4Postcodes";

localize($store.getters.getAppLocale, en);

$store.watch(
  (state) => state.appLocale,
  (newLocale) => {
    if (newLocale === "sv") {
      localize(newLocale, sv);
    }

    if (newLocale === "pl") {
      localize(newLocale, pl);
    }

    if (newLocale === "en") {
      localize(newLocale, en);
    }

    if (newLocale === "no") {
      localize(newLocale, no);
    }
  },
  { immediate: true }
);

// SEC-348: Dutch validation messages
$store.watch(
  (state) => state.appLocale,
  (newLocale) => {
    if (newLocale === "nl") {
      localize(newLocale, nl);
    }
  },
  { immediate: true }
);

extend("required", required);
extend("email", email);
extend("alpha_dash", alpha_dash);
extend("confirmed", confirmed);
extend("oneOf", oneOf);
extend("between", between);
extend("numeric", numeric);
extend("max", max);
extend("min", min);
extend("min_value", min_value);
// SMS numbers in SecurTool format: a supported country code first (46 Sweden,
// 47 Norway, 31 the Netherlands, and the 37x / 35x codes, e.g. 372 Estonia),
// digits only, no spaces or '+'. Empty is allowed — combine with `required`
// where needed. Used by the order form and the SecurTool notification settings
// (customer and unit) so both show the same warning.
extend("sms_msisdn", {
  validate: (value) => !value || /^(46|47|31|37|35)[0-9]+$/.test(String(value)),
  message: () => i18n.t("admin_order_alarm.sms_number_format"),
});
// Contact-hour interval fields (SEC-320): one masked input, "HH:MM-HH:MM".
// Valid when nothing is typed (empty field is allowed) or when both times
// are complete, real clock times. A half-typed interval blocks saving.
extend("time_interval", {
  validate: (value) => {
    const text = String(value == null ? "" : value);
    const hasDigits = /[0-9]/.test(text);
    return !hasDigits || /^([01][0-9]|2[0-3]):[0-5][0-9]-([01][0-9]|2[0-3]):[0-5][0-9]$/.test(text);
  },
  message: () => i18n.t("time_interval.incomplete_error"),
});
// Safe-4 password (SEC-364): only the letters A to Ö, digits and spaces - that
// is A-Z plus Å, Ä and Ö, and nothing else (PM 2026-10-02: no Æ, Ø, É or Ü).
// Lower case is fine while typing, the form converts it to upper case on save.
// Length is handled by `max:20` next to it. Empty is allowed here — combine
// with `required` where the field is mandatory.
extend("safe4_password", {
  validate: (value) => !value || /^[A-Za-zÅÄÖåäö0-9 ]+$/.test(String(value)),
  message: () => i18n.t("change_alarm_plan.password_charset_error"),
});
// SEC-352: an email already used by an earlier account on the same order.
// The earlier emails are the rule's parameters ("not_used_on_order:a@x.se,b@y.se");
// capital letters do not count. Empty is allowed - combine with `required`.
extend("not_used_on_order", {
  validate: (value, earlierEmails) => {
    const email = String(value || "").trim().toLowerCase();
    if (!email) return true;
    const used = earlierEmails.some((earlier) => String(earlier || "").trim().toLowerCase() === email);
    return !used;
  },
  message: () => i18n.t("admin_order_alarm.account_duplicate_email"),
});
// Safe-4 contact number (SEC-364): digits only, so KUIS never receives letters
// or punctuation. The separators the Safe-4 phone field removes itself when the
// user leaves it - spaces, dashes, brackets, dots and slashes - are allowed. A
// "+" is not: the field writes a leading "+" as "00" while it is typed (SEC-373).
// The KUIS mapper formats the number on the way out (SEC-323). Length is handled
// by `max:20` next to it.
extend("safe4_phone", {
  validate: (value) => !value || /^[0-9]+$/.test(String(value).replace(/[\s()\-./]/g, "")),
  message: () => i18n.t("change_alarm_plan.phone_digits_error"),
});
// Safe-4 (SEC-364): the same number may not appear twice on one alarm. The other
// numbers on the alarm are the rule's parameters. The numbers are compared in
// one common form, so the same number written differently still counts:
// "+47 97 28 38 12", "0047 97283812" and "97283812" are one Norwegian number,
// "+46 70 123 45 67" and "070-123 45 67" one Swedish one. That form is for the
// comparison only - what is stored is never changed.
function samePhoneKey(text) {
  let key = String(text || "").trim().replace(/[\s()\-./]/g, "");
  if (key.startsWith("+")) key = "00" + key.slice(1);
  key = key.replace(/[^0-9]/g, "");
  if (key.startsWith("0047")) return key.slice(4);       // Norway: no trunk zero
  if (/^47\d{8}$/.test(key)) return key.slice(2);         // Norway, the 47 written bare
  if (key.startsWith("0046")) return "0" + key.slice(4); // Sweden: trunk zero back
  return key;
}
extend("safe4_phone_unique", {
  validate: (value, otherNumbers) => {
    const mine = samePhoneKey(value);
    if (!mine) return true;
    return !otherNumbers.some((other) => samePhoneKey(other) === mine);
  },
  message: () => i18n.t("change_alarm_plan.phone_duplicate_error"),
});
// Safe-4 (SEC-370): a Norwegian site's postcode must be inside the county
// ranges in safe4Postcodes.js. The parameter is the site country
// ("safe4_postcode:no"); other countries always pass. Empty is allowed -
// combine with `required`.
extend("safe4_postcode", {
  params: ["country"],
  validate: (value, { country }) => {
    if (!value) return true;
    const c = String(country || "").toLowerCase();
    if (c === "no") return isValidNoPostcode(value);
    if (c === "se") return isValidSePostcode(value); // five digits (SEC-373)
    return true;
  },
  message: (field, { country }) => i18n.t(String(country || "").toLowerCase() === "se" ? "safe4_form.invalid_postcode_se" : "safe4_form.invalid_postcode"),
});
